import { useAuth0 } from "@auth0/auth0-react";
import { HiChevronDown } from "react-icons/hi";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  GETDANTE_URL,
  HOME_PAGE_LANDING_ROLES,
  MY_SITE_URL,
  NAV_DATA,
  VALID_PATHS,
} from "constants/navs";
import { MenuItem } from "types/common";

const Header = ({ isEmptyNav }: { isEmptyNav?: boolean }) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth0();
  const permissions = Cookies.get("user_permission")?.split("-") || [];
  const accountType = Cookies.get("accountType");
  const redirectToHome = HOME_PAGE_LANDING_ROLES.includes(accountType);

  const hasPermission = (permissionKey: string): boolean => {
    return permissions.includes(permissionKey);
  };

  /**
   * Special case: Redirect users who have exactly one permission
   * (e.g., Hubspot users) to the corresponding menu item's path or URL.
   * This assumes the permission key matches a menu item's key.
   */
  useEffect(() => {
    if (permissions.length === 1) {
      const userPermission = permissions[0];

      for (const menu of NAV_DATA) {
        if (menu.children) {
          for (const submenu of menu.children) {
            if (submenu.key === userPermission) {
              if (submenu.path) {
                navigate(submenu.path);
              } else if (submenu.url) {
                window.location.href = submenu.url;
              }
              return;
            }
          }
        }
      }
    }
  }, [permissions, navigate]);

  /**
   * Redirect to the main site (MY_SITE_URL) if the current URL path
   * does not match any of the valid OEM frontend routes defined in VALID_PATHS.
   * This ensures no users will be landing on a blank or unsupported page.
   */
  useEffect(() => {
    if (!redirectToHome) return;

    const currentPath = location.pathname.replace(/\/+$/, "");
    const isValid = VALID_PATHS.some(
      (path) => currentPath === path || currentPath.startsWith(path + "/")
    );

    if (!isValid) {
      window.location.replace(MY_SITE_URL);
    }
  }, [location, redirectToHome]);

  return (
    <div className="my__header">
      <div className="my__forceCommunityThemeHeaderCompact my__forceCommunityThemeHeaderBase">
        <div className="my__themeBgImage" />
        <div className="my__themeBottomBorder" />
        <div className="my__themeHeaderInner">
          <div className="my__themeLogo">
            <a className="my__forceCommunityThemeLogo">
              <div className="my__logoImage" />
              <span className="my__slds-assistive-text">DNA Portal</span>
            </a>
          </div>
          {isAuthenticated && !isEmptyNav && (
            <>
              <div className="my__themeNav">
                <ul className="my__nav">
                  {NAV_DATA.map((menu: MenuItem, key: number) => {
                    const visibleSubMenu =
                      menu.children?.filter((child) =>
                        hasPermission(child.key as string)
                      ) || [];

                    if (
                      !permissions.includes("tools") &&
                      visibleSubMenu.length === 1
                    ) {
                      const singleVisibleSubMenu = visibleSubMenu[0];

                      return (
                        <li key={key}>
                          {singleVisibleSubMenu.path ? (
                            <Link to={singleVisibleSubMenu.path}>
                              <button>{singleVisibleSubMenu.name}</button>
                            </Link>
                          ) : singleVisibleSubMenu.url ? (
                            <Link
                              to={singleVisibleSubMenu.url}
                              target={
                                singleVisibleSubMenu.newTab ? "_blank" : "_self"
                              }
                            >
                              <button>{singleVisibleSubMenu.name}</button>
                            </Link>
                          ) : null}
                        </li>
                      );
                    }

                    if (menu.children && visibleSubMenu.length === 0)
                      return null;

                    return (
                      <li key={key}>
                        {menu.url ? (
                          <Link to={menu.url}>
                            <button>{menu.name}</button>
                          </Link>
                        ) : (
                          <button>
                            {menu.name}{" "}
                            <HiChevronDown className="my__nav__icon" />
                          </button>
                        )}
                        {visibleSubMenu.length > 0 && (
                          <ul>
                            {visibleSubMenu.map(
                              (submenu: MenuItem, subkey: number) => {
                                if (submenu.path) {
                                  return (
                                    <li key={subkey}>
                                      <Link to={submenu.path}>
                                        {submenu.name}
                                      </Link>
                                    </li>
                                  );
                                }

                                if (submenu.url) {
                                  return (
                                    <li key={subkey}>
                                      <Link
                                        to={submenu.url}
                                        target={
                                          submenu.newTab ? "_blank" : "_self"
                                        }
                                      >
                                        {submenu.name}
                                      </Link>
                                    </li>
                                  );
                                }

                                return null;
                              }
                            )}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="my__themeUtils">
                <div className="my__themeUtilsContainer">
                  {hasPermission("userProfile") && (
                    <Link to="/profile" style={{ display: 'contents' }}>
                      <img
                        className="my__themeAvatar"
                        src="/avatar.png"
                        alt="avatar"
                      />
                    </Link>
                  )}
                  {!hasPermission("userProfile") && (
                    <img
                      className="my__themeAvatar"
                      src="/avatar.png"
                      alt="avatar"
                    />
                  )}
                  <button className="my__themeProfileMenu">
                    {Cookies.get("user_name")}
                  </button>
                  <ul>
                    {hasPermission("userProfile") && (
                      <li>
                        <Link to="/profile">Profile</Link>
                      </li>
                    )}
                    {hasPermission("userManagement") && (
                      <li>
                        <Link to="/user">Manage Users</Link>
                      </li>
                    )}
                    <li>
                      <a
                        onClick={() =>
                          logout({
                            logoutParams: { returnTo: GETDANTE_URL },
                          })
                        }
                      >
                        Log Out
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
